'use client'

import { useAtomValue, useStore } from 'jotai'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { forwardRef, useCallback, useContext, useEffect, useRef } from 'react'

import { Button } from '@/components/ui/button'
import { useAuthGuard } from '@/features/auth/hooks/use-auth-guard'
import {
  PlanLockTooltip,
  PlanStar,
  usePlanLockMessage,
} from '@/features/billing/components/plan-lock-tooltip'
import type { OrderTabLock } from '@/features/order/order-form/hooks/use-order-tab-lock'
import { useUnifiedOrderFormContext } from '@/features/order/order-form/lib/order-form-context'
import { TutorialPreviewContext } from '@/features/tutorial/lib/tutorial-preview-context'
import { BILLING_ROUTE } from '@/lib/billing/routes'
import { cn } from '@/lib/utils'
import {
  lastFocusedOrderCardAtom,
  orderFormPopupCardIdAtom,
  registeredOrderContainersAtom,
} from '@/state/order'

interface SubmitButtonProps {
  isSubmitting: boolean
  isFormValid: boolean
  isMultiLeg: boolean
  isTwapMode?: boolean
  isLimitChaseMode?: boolean
  isMarketOrder?: boolean
  onSubmit: () => void
  shortcutOwnerCardId?: string
  /** Overrides the default "Submit Order" text (e.g. RFQ mode shows "Request quotes"). */
  label?: string
  /** The order type isn't on the user's plan: the form stays filled but can't be sent. */
  planLock?: OrderTabLock | null
}

export default function SubmitButton({
  isSubmitting,
  isFormValid,
  onSubmit,
  shortcutOwnerCardId,
  label,
  planLock,
}: SubmitButtonProps) {
  const t = useTranslations('orderForm')
  const billingT = useTranslations('billing')
  const planLockMessage = usePlanLockMessage()
  const { cardId } = useUnifiedOrderFormContext()
  const popupCardId = useAtomValue(orderFormPopupCardIdAtom)
  const lastFocusedCard = useAtomValue(lastFocusedOrderCardAtom)
  const registeredContainers = useAtomValue(registeredOrderContainersAtom)
  const buttonText = label ?? (isSubmitting ? t('submittingOrder') : t('submitOrder'))
  const ownerCardId = shortcutOwnerCardId ?? cardId

  // Several Order Forms can be mounted at once; only one may answer the shortcut: the open
  // popup's, else the last focused one's, else the only one there is.
  const ownsShortcut = useCallback(() => {
    if (popupCardId) return popupCardId === ownerCardId
    if (lastFocusedCard) return lastFocusedCard === ownerCardId
    return registeredContainers.size <= 1
  }, [popupCardId, ownerCardId, lastFocusedCard, registeredContainers.size])

  if (planLock) {
    // Locked order types stay visible and filled (e.g. a prompt-drafted TWAP) — only sending is
    // blocked, with the plan that unlocks it and a way to get there.
    const message = planLockMessage(planLock.feature, planLock.plan)
    return (
      <>
        <PlanLockTooltip locked feature={planLock.feature} plan={planLock.plan} className="flex">
          <Button variant="basic" className="h-10 w-full gap-1.5 px-6" disabled aria-disabled>
            <span className="truncate">{message}</span>
            <PlanStar />
          </Button>
        </PlanLockTooltip>
        <Button asChild variant="ghost" size="sm" className="w-full">
          <Link href={BILLING_ROUTE}>
            {planLock.plan ? billingT('upgradeTo', { plan: planLock.plan }) : billingT('upgrade')}
          </Link>
        </Button>
      </>
    )
  }

  return (
    <SubmitActionButton
      label={buttonText}
      onSubmit={onSubmit}
      disabled={isSubmitting || !isFormValid}
      shortcut={ownsShortcut}
    />
  )
}

interface SubmitActionButtonProps {
  label: string
  onSubmit: () => void
  disabled?: boolean
  /**
   * Cmd+Shift+Enter (Ctrl on Windows) also submits, and the ⌘⇧↵ hint is drawn. Pass a
   * predicate to decide per keypress whether this button owns the shortcut (another submit
   * button may be listening too); omit it to leave the key alone and hide the hint.
   */
  shortcut?: () => boolean
  className?: string
}

/**
 * The primary submit action — the Order Form's "Submit Order", and every builder's Start
 * (Arb, Delta Hedge) — so starting something always looks and answers the keyboard the same
 * way: the cyan `basic` button, 40px, full width, with the shortcut hint after the label.
 * Clicks and the shortcut both go through the auth guard.
 */
export const SubmitActionButton = forwardRef<HTMLButtonElement, SubmitActionButtonProps>(
  function SubmitActionButton({ label, onSubmit, disabled = false, shortcut, className }, ref) {
    const { requireAuth } = useAuthGuard()
    const innerRef = useRef<HTMLButtonElement>(null)
    // A tutorial's look-only preview must never answer the submit shortcut.
    const isTutorialPreview = useContext(TutorialPreviewContext)

    const handleKeyDown = useCallback(
      (e: KeyboardEvent) => {
        if (!shortcut || !((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'Enter')) return
        if (!shortcut()) return
        e.preventDefault()
        if (!disabled) requireAuth(onSubmit)
      },
      [shortcut, disabled, onSubmit, requireAuth]
    )

    useEffect(() => {
      if (isTutorialPreview || !shortcut) return
      const targetWindow = innerRef.current?.ownerDocument.defaultView ?? window
      targetWindow.addEventListener('keydown', handleKeyDown)
      return () => targetWindow.removeEventListener('keydown', handleKeyDown)
    }, [handleKeyDown, isTutorialPreview, shortcut])

    return (
      <Button
        ref={(node) => {
          innerRef.current = node
          if (typeof ref === 'function') ref(node)
          else if (ref) ref.current = node
        }}
        variant="basic"
        className={cn('group h-10 w-full px-6', className)}
        disabled={disabled}
        onClick={() => requireAuth(onSubmit)}
      >
        <div className="flex items-center justify-center gap-2">
          <span>{label}</span>
          {shortcut && (
            <svg
              width="38"
              height="17"
              viewBox="0 0 38 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              className="opacity-60 group-disabled:opacity-40"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2.60982 3.53843C2.99778 3.46126 3.39992 3.50087 3.76537 3.65224C4.13082 3.80362 4.44318 4.05996 4.66294 4.38886C4.8827 4.71776 5 5.10444 5 5.5V6.5H7V5.5C7 5.10444 7.1173 4.71776 7.33706 4.38886C7.55682 4.05996 7.86918 3.80362 8.23463 3.65224C8.60009 3.50087 9.00222 3.46126 9.39018 3.53843C9.77814 3.6156 10.1345 3.80608 10.4142 4.08579C10.6939 4.36549 10.8844 4.72186 10.9616 5.10982C11.0387 5.49778 10.9991 5.89992 10.8478 6.26537C10.6964 6.63082 10.44 6.94318 10.1111 7.16294C9.78224 7.3827 9.39556 7.5 9 7.5H8V9.5H9C9.39556 9.5 9.78224 9.6173 10.1111 9.83706C10.44 10.0568 10.6964 10.3692 10.8478 10.7346C10.9991 11.1001 11.0387 11.5022 10.9616 11.8902C10.8844 12.2781 10.6939 12.6345 10.4142 12.9142C10.1345 13.1939 9.77814 13.3844 9.39018 13.4616C9.00222 13.5387 8.60009 13.4991 8.23463 13.3478C7.86918 13.1964 7.55682 12.94 7.33706 12.6111C7.1173 12.2822 7 11.8956 7 11.5V10.5H5V11.5C5 11.8956 4.8827 12.2822 4.66294 12.6111C4.44318 12.94 4.13082 13.1964 3.76537 13.3478C3.39992 13.4991 2.99778 13.5387 2.60982 13.4616C2.22186 13.3844 1.86549 13.1939 1.58579 12.9142C1.30608 12.6345 1.1156 12.2781 1.03843 11.8902C0.96126 11.5022 1.00087 11.1001 1.15224 10.7346C1.30362 10.3692 1.55996 10.0568 1.88886 9.83706C2.21776 9.6173 2.60444 9.5 3 9.5H4V7.5H3C2.60444 7.5 2.21776 7.3827 1.88886 7.16294C1.55996 6.94318 1.30362 6.63082 1.15224 6.26537C1.00087 5.89992 0.96126 5.49778 1.03843 5.10982C1.1156 4.72186 1.30608 4.36549 1.58579 4.08579C1.86549 3.80608 2.22186 3.6156 2.60982 3.53843ZM4 6.5V5.5C4 5.30222 3.94135 5.10888 3.83147 4.94443C3.72159 4.77998 3.56541 4.65181 3.38268 4.57612C3.19996 4.50043 2.99889 4.48063 2.80491 4.51922C2.61093 4.5578 2.43275 4.65304 2.29289 4.79289C2.15304 4.93275 2.0578 5.11093 2.01922 5.30491C1.98063 5.49889 2.00043 5.69996 2.07612 5.88268C2.15181 6.06541 2.27998 6.22159 2.44443 6.33147C2.60888 6.44135 2.80222 6.5 3 6.5H4ZM5 7.5V9.5H7V7.5H5ZM4 10.5H3C2.80222 10.5 2.60888 10.5586 2.44443 10.6685C2.27998 10.7784 2.15181 10.9346 2.07612 11.1173C2.00043 11.3 1.98063 11.5011 2.01922 11.6951C2.0578 11.8891 2.15304 12.0673 2.29289 12.2071C2.43275 12.347 2.61093 12.4422 2.80491 12.4808C2.99889 12.5194 3.19996 12.4996 3.38268 12.4239C3.56541 12.3482 3.72159 12.22 3.83147 12.0556C3.94135 11.8911 4 11.6978 4 11.5V10.5ZM8 10.5V11.5C8 11.6978 8.05865 11.8911 8.16853 12.0556C8.27841 12.22 8.43459 12.3482 8.61732 12.4239C8.80004 12.4996 9.00111 12.5194 9.19509 12.4808C9.38907 12.4422 9.56726 12.347 9.70711 12.2071C9.84696 12.0673 9.9422 11.8891 9.98079 11.6951C10.0194 11.5011 9.99957 11.3 9.92388 11.1173C9.84819 10.9346 9.72002 10.7784 9.55557 10.6685C9.39112 10.5586 9.19778 10.5 9 10.5H8ZM8 6.5H9C9.19778 6.5 9.39112 6.44135 9.55557 6.33147C9.72002 6.22159 9.84819 6.06541 9.92388 5.88268C9.99957 5.69996 10.0194 5.49889 9.98079 5.30491C9.9422 5.11093 9.84696 4.93275 9.70711 4.79289C9.56726 4.65304 9.38907 4.5578 9.19509 4.51922C9.00111 4.48063 8.80004 4.50043 8.61732 4.57612C8.43459 4.65181 8.27841 4.77998 8.16853 4.94443C8.05865 5.10888 8 5.30222 8 5.5V6.5Z"
                fill="currentColor"
              />
              <path
                d="M20.4283 8.87998C20.4283 8.32769 20.876 7.87998 21.4283 7.87998H22.3976L19.3766 4.42929C19.1774 4.20193 18.8233 4.20192 18.6241 4.42929L15.6026 7.87998H16.5724C17.1246 7.87998 17.5724 8.32769 17.5724 8.87998V12.2408C17.5724 12.5169 17.7963 12.7408 18.0724 12.7408H19.9283C20.2045 12.7408 20.4283 12.517 20.4283 12.2408V8.87998ZM21.4283 12.2408C21.4283 13.0692 20.7567 13.7408 19.9283 13.7408H18.0724C17.244 13.7408 16.5724 13.0692 16.5724 12.2408V8.87998H15.6026C14.7433 8.87998 14.284 7.86729 14.8502 7.2208L17.8717 3.7706C18.4693 3.0882 19.5314 3.08817 20.129 3.7706L23.15 7.22128C23.716 7.86779 23.2569 8.87998 22.3976 8.87998H21.4283V12.2408Z"
                fill="currentColor"
              />
              <path
                d="M30.1191 7.00098C30.3234 6.81521 30.6399 6.83045 30.8257 7.03467C31.0113 7.23892 30.9962 7.55494 30.792 7.74072L28.792 9.56055H35.5C35.7761 9.56055 36 9.33666 36 9.06055V5.25C36 4.97386 35.7761 4.75 35.5 4.75H33.5181C33.2419 4.75 33.0181 4.52614 33.0181 4.25C33.0181 3.97386 33.2419 3.75 33.5181 3.75H35.5C36.3284 3.75 37 4.42157 37 5.25V9.06055C37 9.88895 36.3284 10.5605 35.5 10.5605H28.792L30.792 12.3804C30.9962 12.5662 31.0114 12.8822 30.8257 13.0864C30.6398 13.2907 30.3234 13.3055 30.1191 13.1196L27.1636 10.4302C27.0595 10.3354 27 10.2013 27 10.0605C27 9.91979 27.0595 9.78567 27.1636 9.69092L30.1191 7.00098Z"
                fill="currentColor"
              />
            </svg>
          )}
        </div>
      </Button>
    )
  }
)

/**
 * The shortcut predicate for a submit button outside any Order Form (a builder's Start). Only
 * where that builder is the page itself — `enabled` false in a dialog, which usually opens over
 * a trade page whose Order Form listens for the same keys — and never while the Order Form
 * popup is open, since then the key belongs to it.
 */
export function useStandaloneSubmitShortcut(enabled: boolean): (() => boolean) | undefined {
  const store = useStore()
  const ownsShortcut = useCallback(() => !store.get(orderFormPopupCardIdAtom), [store])
  return enabled ? ownsShortcut : undefined
}
